import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import RegistrationHeader from './components/RegistrationHeader';
import ProgressIndicator from './components/ProgressIndicator';
import AgencyInfoForm from './components/AgencyInfoForm';
import AdminAccountForm from './components/AdminAccountForm';
import PlanSelection from './components/PlanSelection';
import DocumentUpload from './components/DocumentUpload';
import TermsAcceptance from './components/TermsAcceptance';
import TrustBadges from './components/TrustBadges';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';

const AgencyRegistration = () => {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [billingCycle, setBillingCycle] = useState('annual');

  const [formData, setFormData] = useState({
    agencyName: '',
    licenseNumber: '',
    city: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    firstName: '',
    lastName: '',
    adminEmail: '',
    adminPhone: '',
    password: '',
    confirmPassword: '',
    selectedPlan: 'professional'
  });

  const [documents, setDocuments] = useState({
    license: null,
    certification: null
  });

  const [acceptedTerms, setAcceptedTerms] = useState({
    terms: false,
    islamic: false,
    saudi: false,
    data: false
  });

  const [errors, setErrors] = useState({});

  const content = {
    fr: {
      steps: [
        'Informations agence',
        'Compte administrateur',
        'Choix du plan',
        'Documents',
        'Finalisation'
      ],
      nextButton: 'Suivant',
      previousButton: 'Précédent',
      submitButton: 'Créer mon compte',
      submitting: 'Création en cours...'
    },
    ar: {
      steps: [
        'معلومات الوكالة',
        'حساب المسؤول',
        'اختيار الخطة',
        'الوثائق',
        'الإنهاء'
      ],
      nextButton: 'التالي',
      previousButton: 'السابق',
      submitButton: 'إنشاء حسابي',
      submitting: 'جاري الإنشاء...'
    }
  };

  const text = content?.[currentLanguage];

  const subscriptionPlans = [
    {
      id: 'starter',
      name: { fr: 'Starter', ar: 'المبتدئ' },
      description: { fr: 'Pour les petites agences', ar: 'للوكالات الصغيرة' },
      monthlyPrice: '499',
      annualPrice: '4,790',
      isPopular: false,
      features: {
        fr: [
          'Jusqu\'à 50 pèlerins',
          '2 utilisateurs',
          'Gestion de base',
          'Support email',
          'Stockage 5GB'
        ],
        ar: [
          'حتى 50 حاج',
          'مستخدمين 2',
          'إدارة أساسية',
          'دعم البريد الإلكتروني',
          'تخزين 5 جيجابايت'
        ]
      }
    },
    {
      id: 'professional',
      name: { fr: 'Professionnel', ar: 'المحترف' },
      description: { fr: 'Pour les agences en croissance', ar: 'للوكالات المتنامية' },
      monthlyPrice: '999',
      annualPrice: '9,590',
      isPopular: true,
      features: {
        fr: [
          'Jusqu\'à 200 pèlerins',
          '5 utilisateurs',
          'Toutes les fonctionnalités',
          'Support prioritaire',
          'Stockage 20GB',
          'Intégration Nusuk',
          'Rapports avancés'
        ],
        ar: [
          'حتى 200 حاج',
          '5 مستخدمين',
          'جميع الميزات',
          'دعم ذو أولوية',
          'تخزين 20 جيجابايت',
          'تكامل نسك',
          'تقارير متقدمة'
        ]
      }
    },
    {
      id: 'enterprise',
      name: { fr: 'Entreprise', ar: 'المؤسسة' },
      description: { fr: 'Pour les grandes agences', ar: 'للوكالات الكبيرة' },
      monthlyPrice: '1,999',
      annualPrice: '19,190',
      isPopular: false,
      features: {
        fr: [
          'Pèlerins illimités',
          'Utilisateurs illimités',
          'Fonctionnalités premium',
          'Support 24/7',
          'Stockage illimité',
          'API personnalisée',
          'Formation dédiée',
          'Gestionnaire de compte'
        ],
        ar: [
          'حجاج غير محدودين',
          'مستخدمين غير محدودين',
          'ميزات متميزة',
          'دعم 24/7',
          'تخزين غير محدود',
          'API مخصص',
          'تدريب مخصص',
          'مدير حساب'
        ]
      }
    }
  ];

  const handleLanguageChange = () => {
    setCurrentLanguage(currentLanguage === 'fr' ? 'ar' : 'fr');
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handlePlanSelect = (planId) => {
    setFormData(prev => ({ ...prev, selectedPlan: planId }));
  };

  const handleDocumentUpload = (type, file) => {
    setDocuments(prev => ({ ...prev, [type]: file }));
  };

  const handleDocumentRemove = (type) => {
    setDocuments(prev => ({ ...prev, [type]: null }));
  };

  const handleTermsChange = (termId, checked) => {
    setAcceptedTerms(prev => ({ ...prev, [termId]: checked }));
    if (errors?.[termId]) {
      setErrors(prev => ({ ...prev, [termId]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.agencyName?.trim()) {
        newErrors.agencyName = currentLanguage === 'fr' ?'Le nom de l\'agence est requis' :'اسم الوكالة مطلوب';
      }
      if (!formData?.licenseNumber?.trim()) {
        newErrors.licenseNumber = currentLanguage === 'fr' ?'Le numéro de licence est requis' :'رقم الترخيص مطلوب';
      }
      if (!formData?.city) {
        newErrors.city = currentLanguage === 'fr' ?'La ville est requise' :'المدينة مطلوبة';
      }
      if (!formData?.address?.trim()) {
        newErrors.address = currentLanguage === 'fr' ?'L\'adresse est requise' :'العنوان مطلوب';
      }
      if (!formData?.phone?.trim()) {
        newErrors.phone = currentLanguage === 'fr' ?'Le téléphone est requis' :'الهاتف مطلوب';
      }
      if (!formData?.email?.trim() || !/\S+@\S+\.\S+/?.test(formData?.email)) {
        newErrors.email = currentLanguage === 'fr' ?'Email valide requis' :'البريد الإلكتروني الصحيح مطلوب';
      }
    }

    if (step === 2) {
      if (!formData?.firstName?.trim()) {
        newErrors.firstName = currentLanguage === 'fr' ?'Le prénom est requis' :'الاسم الأول مطلوب';
      }
      if (!formData?.lastName?.trim()) {
        newErrors.lastName = currentLanguage === 'fr' ?'Le nom est requis' :'اسم العائلة مطلوب';
      }
      if (!formData?.adminEmail?.trim() || !/\S+@\S+\.\S+/?.test(formData?.adminEmail)) {
        newErrors.adminEmail = currentLanguage === 'fr' ?'Email valide requis' :'البريد الإلكتروني الصحيح مطلوب';
      }
      if (!formData?.adminPhone?.trim()) {
        newErrors.adminPhone = currentLanguage === 'fr' ?'Le téléphone est requis' :'الهاتف مطلوب';
      }
      if (!formData?.password || formData?.password?.length < 8) {
        newErrors.password = currentLanguage === 'fr' ?'Le mot de passe doit contenir au moins 8 caractères' :'يجب أن تحتوي كلمة المرور على 8 أحرف على الأقل';
      }
      if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = currentLanguage === 'fr' ?'Les mots de passe ne correspondent pas' :'كلمات المرور غير متطابقة';
      }
    }

    if (step === 4) {
      if (!documents?.license) {
        newErrors.license = currentLanguage === 'fr' ?'La licence d\'agence est requise' :'ترخيص الوكالة مطلوب';
      }
    }

    if (step === 5) {
      if (!acceptedTerms?.terms) {
        newErrors.terms = currentLanguage === 'fr' ?'Vous devez accepter les conditions' :'يجب عليك قبول الشروط';
      }
      if (!acceptedTerms?.islamic) {
        newErrors.islamic = currentLanguage === 'fr' ?'Confirmation requise' :'التأكيد مطلوب';
      }
      if (!acceptedTerms?.saudi) {
        newErrors.saudi = currentLanguage === 'fr' ?'Engagement requis' :'الالتزام مطلوب';
      }
      if (!acceptedTerms?.data) {
        newErrors.data = currentLanguage === 'fr' ?'Acceptation requise' :'القبول مطلوب';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 5));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(5)) return;

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const result = await signUp(formData?.adminEmail, formData?.password, {
        full_name: `${formData?.firstName} ${formData?.lastName}`,
        agency_name: formData?.agencyName,
        agency: formData?.agencyName,
        role: 'agency_admin',
        phone: formData?.adminPhone,
        plan: formData?.selectedPlan,
        license_number: formData?.licenseNumber,
        city: formData?.city,
      });

      // Check if email confirmation is required
      if (result?.user && !result?.session) {
        // Email confirmation required — show message instead of redirecting
        setSubmitError('');
        setCurrentStep(6); // Use step 6 as a "success" state
      } else {
        // Auto-confirmed — redirect to dashboard
        navigate('/agency-dashboard');
      }
    } catch (error) {
      console.error('Registration error:', error);
      const msg = error?.message || '';
      let displayError;
      if (msg?.toLowerCase()?.includes('already registered') || msg?.toLowerCase()?.includes('already exists') || msg?.toLowerCase()?.includes('user already')) {
        displayError = currentLanguage === 'fr' ?'Cet email est déjà utilisé. Veuillez vous connecter ou utiliser un autre email.' :'هذا البريد الإلكتروني مستخدم بالفعل. يرجى تسجيل الدخول أو استخدام بريد آخر.';
      } else if (msg?.toLowerCase()?.includes('password')) {
        displayError = currentLanguage === 'fr' ?'Le mot de passe ne respecte pas les critères requis (minimum 6 caractères).' :'كلمة المرور لا تستوفي المتطلبات (6 أحرف على الأقل).';
      } else if (msg?.toLowerCase()?.includes('network') || msg?.toLowerCase()?.includes('fetch')) {
        displayError = currentLanguage === 'fr' ?'Erreur de connexion réseau. Vérifiez votre connexion internet.' :'خطأ في الاتصال بالشبكة. تحقق من اتصالك بالإنترنت.';
      } else {
        displayError = currentLanguage === 'fr' ? `Erreur lors de l'inscription: ${msg}`
          : `خطأ في التسجيل: ${msg}`;
      }
      setSubmitError(displayError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <AgencyInfoForm
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
            currentLanguage={currentLanguage}
          />
        );
      case 2:
        return (
          <AdminAccountForm
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
            currentLanguage={currentLanguage}
          />
        );
      case 3:
        return (
          <PlanSelection
            plans={subscriptionPlans}
            selectedPlan={formData?.selectedPlan}
            onSelectPlan={handlePlanSelect}
            billingCycle={billingCycle}
            onBillingCycleChange={setBillingCycle}
            currentLanguage={currentLanguage}
          />
        );
      case 4:
        return (
          <DocumentUpload
            documents={documents}
            onDocumentUpload={handleDocumentUpload}
            onDocumentRemove={handleDocumentRemove}
            currentLanguage={currentLanguage}
          />
        );
      case 5:
        return (
          <TermsAcceptance
            acceptedTerms={acceptedTerms}
            onTermsChange={handleTermsChange}
            errors={errors}
            currentLanguage={currentLanguage}
          />
        );
      case 6:
        // Registration success — email confirmation pending
        return (
          <div className="text-center py-12 space-y-6">
            <div className="w-20 h-20 rounded-full bg-success/10 flex items-center justify-center mx-auto">
              <Icon name="CheckCircle" size={40} className="text-success" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                {currentLanguage === 'fr' ? 'Inscription réussie !' : 'تم التسجيل بنجاح!'}
              </h2>
              <p className="text-muted-foreground max-w-md mx-auto">
                {currentLanguage === 'fr'
                  ? `Un email de confirmation a été envoyé à ${formData?.adminEmail}. Cliquez sur le lien pour activer votre compte et accéder au tableau de bord.`
                  : `تم إرسال بريد إلكتروني للتأكيد إلى ${formData?.adminEmail}. انقر على الرابط لتفعيل حسابك والوصول إلى لوحة التحكم.`}
              </p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 border border-border text-sm text-left max-w-sm mx-auto space-y-1">
              <p className="font-semibold text-foreground">
                {currentLanguage === 'fr' ? '📧 Vos identifiants :' : '📧 بيانات الدخول:'}
              </p>
              <p className="text-muted-foreground">
                Email: <span className="font-mono text-foreground">{formData?.adminEmail}</span>
              </p>
              <p className="text-muted-foreground">
                {currentLanguage === 'fr' ? 'Mot de passe' : 'كلمة المرور'}: <span className="font-mono text-foreground">{formData?.password}</span>
              </p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors"
            >
              <Icon name="LogIn" size={18} />
              {currentLanguage === 'fr' ? 'Aller à la page de connexion' : 'الذهاب إلى صفحة تسجيل الدخول'}
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  const steps = text?.steps?.map((label, index) => ({
    fr: content?.fr?.steps?.[index],
    ar: content?.ar?.steps?.[index]
  }));

  return (
    <div className="min-h-screen bg-background">
      <RegistrationHeader
        currentLanguage={currentLanguage}
        onLanguageChange={handleLanguageChange}
      />
      <main className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
        <ProgressIndicator
          currentStep={currentStep}
          totalSteps={5}
          steps={steps}
          currentLanguage={currentLanguage}
        />

        <div className="bg-card rounded-xl shadow-warm-lg p-4 md:p-6 lg:p-8 mb-6 md:mb-8">
          {renderStepContent()}
        </div>

        {currentStep === 3 && (
          <div className="mb-6 md:mb-8">
            <TrustBadges currentLanguage={currentLanguage} />
          </div>
        )}

        {submitError && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-center gap-2">
            <Icon name="AlertCircle" size={18} className="text-destructive flex-shrink-0" />
            <p className="text-sm text-destructive">{submitError}</p>
          </div>
        )}

        {currentStep < 6 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {currentStep > 1 && (
              <Button
                variant="outline"
                onClick={handlePrevious}
                iconName="ChevronLeft"
                iconPosition="left"
                className="w-full sm:w-auto"
              >
                {text?.previousButton}
              </Button>
            )}

            <div className="flex-1" />

            {currentStep < 5 ? (
              <Button
                variant="default"
                onClick={handleNext}
                iconName="ChevronRight"
                iconPosition="right"
                className="w-full sm:w-auto"
              >
                {text?.nextButton}
              </Button>
            ) : (
              <Button
                variant="default"
                onClick={handleSubmit}
                loading={isSubmitting}
                iconName="Check"
                iconPosition="right"
                className="w-full sm:w-auto"
              >
                {isSubmitting ? text?.submitting : text?.submitButton}
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AgencyRegistration;